// [PURPOSE]: Settings panel - extracted from App.jsx into a standalone component
// [WHY]: Session 3 of release plan - reorganize Settings tab into 8 target sections
// [SESSIONS REFERENCED]: Sessions 5 & 7 placeholders for future features
import React, { useState, useCallback } from 'react';
import {
  Eye, Info, Book, Dices, Sparkles,
  Save, Upload, Lock, Download, Mail, Terminal, FileText,
  Trash2, AlertTriangle, ChevronDown, ChevronUp, ExternalLink,
  Image as ImageIcon, BookOpen, Palette
} from 'lucide-react';
import {
  addImage,
  removeImage,
  buildImageBook,
  importImageBook,
  libraryBytes,
  fileToBase64,
  setAsActivePortrait, // [STONE 7 / #79 — 2026-05-18]: Integrate gallery → basics.portrait
} from '../utils/imageLibrary';
import { APP_VERSION } from '../constants/version';
import {
  TAB_LABELS,
  TAB_GROUPS,
  ALWAYS_VISIBLE_TABS,
  getToggleableTabsByGroup,
} from '../config/tabGroups';
import { safeLocalStorageSet } from '../utils/storageValidator';
import { applyRuleEditionPreset } from '../utils/ruleEditionPreset';
import { AVAILABLE_THEMES } from '../utils/themeRegistry';
import CollapsibleSection from './CollapsibleSection';
import AccountPanel from './AccountPanel';
import PremiumGate from './PremiumGate';
import SupportSection from './SupportSection';
import SourcesPanel from './SourcesPanel';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useUI } from '../contexts/UIContext';
import { useCharacter } from '../contexts/CharacterContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useTutorial } from '../contexts/TutorialContext';
import TutorialHelpMenu from './tutorial/TutorialHelpMenu';
import { DISPLAY_MODES } from '../constants/enums';
import { useModalEscape } from '../hooks/useModalEscape';

export default function SettingsPanel({
  // Character management functions (App.jsx-local)
  clearAutosave,
  nuclearReset,
  exportCharacter,
  exportDebugBundle,
  importInventory,
  saveCharacter,
  loadCharacter,
  deleteCharacter,
  duplicateCharacter,
}) {
  const { currentUser, isAuthenticated, isPremium, deleteAccount, exportAllUserData, reauthenticate, refreshPremiumStatus } = useAuth();
  const { settings, setSettings, savedCharacters, resetSettings } = useSettings();
  const { showStatus } = useUI();
  const {
    playerStats, setPlayerStats, logActivity,
    timeSystem, setTimeSystem,
    setKnownSpells, setPreparedSpells, setSpellSlots,
    setShowCharacterWizard,
  } = useCharacter();
  const { confirm } = useConfirm();
  const { resetAllTutorials } = useTutorial();

  // [S5]: Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showReauthPrompt, setShowReauthPrompt] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  // [S5]: Export loading state
  const [exportLoading, setExportLoading] = useState(false);
  // [S5]: OGL attribution collapsible state
  const [showOglContent, setShowOglContent] = useState(false);
  // [TUTORIAL-1]: Browse / Replay Tours modal state
  const [showTutorialHelpMenu, setShowTutorialHelpMenu] = useState(false);

  // [APR 2026]: Standardize Escape dismissal for the Delete Account modal.
  // [WHY]: Mirrors the Cancel button — clears both the modal AND the reauth prompt
  //   to match the existing handler at line 1249.
  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setShowReauthPrompt(false);
  }, []);
  useModalEscape(showDeleteModal, closeDeleteModal);

  // [S5]: Handle account deletion with re-auth support
  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleteLoading(true);
    const result = await deleteAccount(false);
    setDeleteLoading(false);

    if (result.success) {
      // Account deleted — auth state change will handle UI
      showStatus('Account deleted successfully. Your data has been exported.', 5000);
    } else if (result.error === 'requires-recent-login') {
      // Need re-authentication
      const providerId = currentUser?.providerData?.[0]?.providerId;
      if (providerId === 'google.com') {
        // Google users re-auth via popup automatically
        setDeleteError('');
        setDeleteLoading(true);
        const reauth = await reauthenticate();
        if (reauth.success) {
          // Retry deletion after re-auth
          const retry = await deleteAccount(true);
          setDeleteLoading(false);
          if (retry.success) {
            showStatus('Account deleted successfully. Your data has been exported.', 5000);
          } else {
            setDeleteError(retry.error || 'Failed to delete account after re-authentication.');
          }
        } else {
          setDeleteLoading(false);
          setDeleteError(reauth.error || 'Re-authentication failed.');
        }
      } else {
        // Email users need to enter password
        setShowReauthPrompt(true);
      }
    } else {
      setDeleteError(result.error || 'Failed to delete account. Please try again.');
    }
  };

  // [S5]: Handle re-auth with password, then retry deletion
  const handleReauthAndDelete = async () => {
    setDeleteError('');
    setDeleteLoading(true);
    const reauth = await reauthenticate(reauthPassword);
    if (reauth.success) {
      const retry = await deleteAccount(true);
      setDeleteLoading(false);
      if (retry.success) {
        showStatus('Account deleted successfully. Your data has been exported.', 5000);
      } else {
        setDeleteError(retry.error || 'Failed to delete account after re-authentication.');
      }
    } else {
      setDeleteLoading(false);
      setDeleteError(reauth.error || 'Incorrect password.');
    }
  };

  // [S5]: Handle full data export
  const handleExportAllData = async () => {
    setExportLoading(true);
    const result = await exportAllUserData();
    setExportLoading(false);
    if (result.success) {
      showStatus('Data exported! Check your downloads folder.', 3000);
    } else {
      showStatus('Failed to export data: ' + (result.error || 'Unknown error'), 5000);
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h2 className="text-2xl font-bold mb-4">Settings</h2>
      <div className="space-y-3">

        {/* ─── 1. Display ─── */}
        <CollapsibleSection
          title="Display"
          icon={<Eye size={16} />}
          storageKey="settings-display"
          defaultCollapsed={false}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">

            {/* Display Mode Selector - Three-tier view system
                [APR 2026 RE-ADDRESS]: Descriptions match the actual scope after
                the SIMPLE/JUST_INVENTORY trim. DISPLAY_MODES enum used everywhere
                instead of bare strings (Rule #11). */}
            <div className="bg-blue-900 p-4 rounded-lg border border-blue-600 mb-4" data-tutorial="settings-display">
              <h4 className="font-bold text-white mb-3">📱 Display Mode</h4>
              <div className="space-y-2">
                {/* Default View */}
                <label className={`flex items-start cursor-pointer p-2 rounded transition-colors ${settings.displayMode === DISPLAY_MODES.DEFAULT ? 'bg-blue-800/30 ring-1 ring-blue-500/50' : 'hover:bg-blue-800/50'}`}>
                  <input
                    type="radio"
                    name="displayMode"
                    value={DISPLAY_MODES.DEFAULT}
                    checked={settings.displayMode === DISPLAY_MODES.DEFAULT}
                    onChange={() => {
                      // [BATCH 13b / P1 D-32 — APR 2026]: Log display-mode
                      //   changes per Rule 13. Pre-fix, retro-debugging
                      //   "what changed in my UI" had no answer.
                      setSettings(prev => ({
                        ...prev,
                        displayMode: DISPLAY_MODES.DEFAULT
                      }));
                      showStatus('🛠️ Default View - All features available', 3000);
                      logActivity('Display mode set to Default View', 'settings', { displayMode: DISPLAY_MODES.DEFAULT });
                    }}
                    className="mt-1 mr-3 accent-blue-500"
                  />
                  <div>
                    <span className="font-semibold text-white">Default View</span>
                    <div className="text-xs text-gray-300">Full character sheet with all features.</div>
                  </div>
                </label>

                {/* Simple Mode */}
                <label className={`flex items-start cursor-pointer p-2 rounded transition-colors ${settings.displayMode === DISPLAY_MODES.SIMPLE ? 'bg-blue-800/30 ring-1 ring-blue-500/50' : 'hover:bg-blue-800/50'}`}>
                  <input
                    type="radio"
                    name="displayMode"
                    value={DISPLAY_MODES.SIMPLE}
                    checked={settings.displayMode === DISPLAY_MODES.SIMPLE}
                    onChange={() => {
                      // [BATCH 13b / P1 D-32 — APR 2026]: Rule 13.
                      setSettings(prev => ({
                        ...prev,
                        displayMode: DISPLAY_MODES.SIMPLE
                      }));
                      showStatus('🌟 Simple Mode - Streamlined for play', 3000);
                      logActivity('Display mode set to Simple Mode', 'settings', { displayMode: DISPLAY_MODES.SIMPLE });
                    }}
                    className="mt-1 mr-3 accent-blue-500"
                  />
                  <div>
                    <span className="font-semibold text-white">Simple Mode</span>
                    <div className="text-xs text-gray-300">Streamlined for play; hides character-build tools.</div>
                  </div>
                </label>

                {/* Just The Inventory */}
                <label className={`flex items-start cursor-pointer p-2 rounded transition-colors ${settings.displayMode === DISPLAY_MODES.JUST_INVENTORY ? 'bg-blue-800/30 ring-1 ring-blue-500/50' : 'hover:bg-blue-800/50'}`}>
                  <input
                    type="radio"
                    name="displayMode"
                    value={DISPLAY_MODES.JUST_INVENTORY}
                    checked={settings.displayMode === DISPLAY_MODES.JUST_INVENTORY}
                    onChange={() => {
                      // [BATCH 13b / P1 D-32 — APR 2026]: Rule 13.
                      setSettings(prev => ({
                        ...prev,
                        displayMode: DISPLAY_MODES.JUST_INVENTORY
                      }));
                      showStatus('📦 Just The Inventory - Inventory grid only', 3000);
                      logActivity('Display mode set to Just The Inventory', 'settings', { displayMode: DISPLAY_MODES.JUST_INVENTORY });
                    }}
                    className="mt-1 mr-3 accent-blue-500"
                  />
                  <div>
                    <span className="font-semibold text-white">Just The Inventory</span>
                    <div className="text-xs text-gray-300">Inventory grid only.</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Quick Actions Bar Settings */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-purple-600">
              <h3 className="text-lg font-bold mb-3 text-purple-300">⚡ Quick Actions Bar</h3>

              <label className="flex items-center text-gray-300 mb-3">
                <input
                  type="checkbox"
                  checked={settings.showQuickActions}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      showQuickActions: e.target.checked
                    }));
                    showStatus(e.target.checked ?
                      '⚡ Quick Actions Bar enabled' :
                      '⚡ Quick Actions Bar disabled', 2000);
                  }}
                  className="mr-3"
                />
                <div>
                  <span className="font-bold text-white">Enable Quick Actions Bar</span>
                  <div className="text-xs text-gray-400">Sliding panel for quick rolls, saving throws, and skill checks</div>
                </div>
              </label>

              {settings.showQuickActions && (
                <div className="ml-6 space-y-2">
                  <p className="text-xs text-gray-300">
                    <span className="font-bold">💡 Tip:</span> Drag the ⚡ lightning bolt icon anywhere on your screen to reposition it!
                  </p>
                  <button
                    onClick={() => {
                      setSettings(prev => ({
                        ...prev,
                        quickActionsTabPosition: null
                      }));
                      showStatus('Quick Actions position reset - will appear on right side', 2000);
                    }}
                    className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
                  >
                    Reset Position
                  </button>
                </div>
              )}

              {/* [P1.7 APR 2026]: Configure Quick Actions — per-action visibility.
                  Restores capability lost when QuickActionsBar was archived.
                  Each toggle hides/shows a section in the bar. Defaults to all
                  visible; backwards-compatible for existing users. */}
              {settings.showQuickActions && (
                <div className="mt-4 pt-4 border-t border-purple-700/40">
                  <h4 className="text-md font-bold text-purple-200 mb-2">Configure Quick Actions</h4>
                  <p className="text-xs text-gray-400 mb-3">
                    Hide individual rolls and sections you don't use. All sections show by default.
                  </p>

                  {(() => {
                    const v = settings.quickActionsVisibility || {};
                    const setVis = (mutator) => {
                      setSettings(prev => {
                        const prevVis = prev.quickActionsVisibility || {};
                        return {
                          ...prev,
                          quickActionsVisibility: mutator(prevVis)
                        };
                      });
                    };

                    const setTopFlag = (key, value) => setVis(prev => ({ ...prev, [key]: value }));
                    const setSaveFlag = (stat, value) => setVis(prev => ({
                      ...prev,
                      saves: { ...(prev.saves || {}), [stat]: value }
                    }));
                    const setSkillFlag = (skill, value) => setVis(prev => ({
                      ...prev,
                      skills: { ...(prev.skills || {}), [skill]: value }
                    }));

                    const ABILITIES = [
                      { key: 'str', label: 'STR' },
                      { key: 'dex', label: 'DEX' },
                      { key: 'con', label: 'CON' },
                      { key: 'int', label: 'INT' },
                      { key: 'wis', label: 'WIS' },
                      { key: 'cha', label: 'CHA' }
                    ];
                    const SKILLS = ['Perception', 'Investigation', 'Stealth', 'Athletics', 'Acrobatics', 'Insight'];

                    const allOn = () => setSettings(prev => ({
                      ...prev,
                      quickActionsVisibility: {
                        initiative: true,
                        combatToggles: true,
                        saves: { str: true, dex: true, con: true, int: true, wis: true, cha: true },
                        skills: SKILLS.reduce((acc, s) => ({ ...acc, [s]: true }), {}),
                        hitDice: true,
                        quickDice: true
                      }
                    }));
                    const allOff = () => setSettings(prev => ({
                      ...prev,
                      quickActionsVisibility: {
                        initiative: false,
                        combatToggles: false,
                        saves: { str: false, dex: false, con: false, int: false, wis: false, cha: false },
                        skills: SKILLS.reduce((acc, s) => ({ ...acc, [s]: false }), {}),
                        hitDice: false,
                        quickDice: false
                      }
                    }));

                    return (
                      <div className="space-y-3">
                        {/* Top-level sections */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/40 p-1.5 rounded">
                            <input
                              type="checkbox"
                              checked={v.initiative !== false}
                              onChange={(e) => setTopFlag('initiative', e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-gray-200">Initiative</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/40 p-1.5 rounded">
                            <input
                              type="checkbox"
                              checked={v.combatToggles !== false}
                              onChange={(e) => setTopFlag('combatToggles', e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-gray-200">Combat Toggles</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/40 p-1.5 rounded">
                            <input
                              type="checkbox"
                              checked={v.hitDice !== false}
                              onChange={(e) => setTopFlag('hitDice', e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-gray-200">Hit Dice (Recovery)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/40 p-1.5 rounded">
                            <input
                              type="checkbox"
                              checked={v.quickDice !== false}
                              onChange={(e) => setTopFlag('quickDice', e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-gray-200">Quick Dice</span>
                          </label>
                        </div>

                        {/* Saving Throws */}
                        <div>
                          <h5 className="text-xs font-semibold text-gray-300 mb-1.5">Saving Throws</h5>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            {ABILITIES.map(({ key, label }) => (
                              <label
                                key={key}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/40 p-1.5 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={v.saves?.[key] !== false}
                                  onChange={(e) => setSaveFlag(key, e.target.checked)}
                                  className="rounded"
                                />
                                <span className="text-gray-200">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Common Skills */}
                        <div>
                          <h5 className="text-xs font-semibold text-gray-300 mb-1.5">Common Skills</h5>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {SKILLS.map(skill => (
                              <label
                                key={skill}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/40 p-1.5 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={v.skills?.[skill] !== false}
                                  onChange={(e) => setSkillFlag(skill, e.target.checked)}
                                  className="rounded"
                                />
                                <span className="text-gray-200">{skill}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Show All / Hide All shortcuts */}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => { allOn(); showStatus('⚡ All Quick Actions shown', 2000); }}
                            className="px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded"
                          >
                            Show All
                          </button>
                          <button
                            onClick={() => { allOff(); showStatus('⚡ All Quick Actions hidden', 2000); }}
                            className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded"
                          >
                            Hide All
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Tab Visibility */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-indigo-600" data-tutorial="settings-tabs">
              <h3 className="text-lg font-bold mb-3 text-indigo-300 flex items-center gap-2">
                📑 Tab Visibility
                {/* [F-016 FIX 2026-05-09]: Show a count badge of currently-hidden tabs
                    so users who hid tabs and forgot can see at a glance there are tabs
                    they're not seeing in their nav. Recoverable from this panel. */}
                {settings.hiddenTabs?.length > 0 && (
                  <span className="text-xs bg-amber-700/40 text-amber-300 px-2 py-0.5 rounded-full font-normal">
                    {settings.hiddenTabs.length} hidden
                  </span>
                )}
              </h3>
              {/* [4B-tab-visibility-settings — 2026-05-30]: Tab list is data-driven
                  from tabGroups.js (TAB_TO_GROUP + TAB_GROUPS + TAB_LABELS).
                  Replacing the prior hardcoded 17-tab array which was missing
                  'combat' and used capitalize() for labels (wrong for
                  'spellbook'→'Spells', 'magic'→'Magic Items', etc.).
                  Always-visible tabs (character/workshop/settings) are excluded
                  from the grid; they are enumerated in ALWAYS_VISIBLE_TABS. */}
              <p className="text-sm text-gray-400 mb-3">
                Hide tabs you don't use to simplify navigation.{' '}
                {ALWAYS_VISIBLE_TABS.map(t => TAB_LABELS[t]).join(', ')} are always visible.
              </p>
              {getToggleableTabsByGroup().map(({ group, tabs }) => (
                <div key={group.id} className="mb-4" data-testid={`tab-visibility-group-${group.id}`}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {group.label}
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {tabs.map(tab => (
                      <label
                        key={tab}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors"
                        data-testid={`tab-visibility-toggle-${tab}`}
                      >
                        <input
                          type="checkbox"
                          checked={!settings.hiddenTabs?.includes(tab)}
                          onChange={(e) => {
                            setSettings(prev => ({
                              ...prev,
                              hiddenTabs: e.target.checked
                                ? (prev.hiddenTabs || []).filter(t => t !== tab)
                                : [...(prev.hiddenTabs || []), tab]
                            }));
                          }}
                          className="rounded"
                          data-testid={`tab-visibility-checkbox-${tab}`}
                        />
                        <span className="text-gray-300">{TAB_LABELS[tab] ?? tab}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => {
                    setSettings(prev => ({ ...prev, hiddenTabs: [] }));
                    showStatus('📑 All tabs visible', 2000);
                  }}
                  className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded"
                >
                  Show All
                </button>
                <button
                  onClick={() => {
                    setSettings(prev => ({
                      ...prev,
                      hiddenTabs: ['downtimes', 'calendar', 'bastion', 'party', 'journal', 'tables', 'log']
                    }));
                    showStatus('📑 Simplified view (core tabs only)', 2000);
                  }}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded"
                >
                  Simplified
                </button>
              </div>

              {/* [A17 — 2026-05-25]: Roster tab feature flag. Default OFF until
                  the feature is finished. User report 5/17 — "Roster tab
                  unsolicited + disconnected + broken — hide behind flag".
                  MobileNavigation filters the nav entry on this flag and
                  App.jsx gates the render block on the same flag. */}
              <div className="mt-4 pt-3 border-t border-gray-700">
                <label className="flex items-start text-gray-300 cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={settings.enableRosterTab || false}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setSettings(prev => ({
                        ...prev,
                        enableRosterTab: enabled
                      }));
                      showStatus(enabled ?
                        '👥 Roster tab enabled (work-in-progress feature)' :
                        '👥 Roster tab hidden', 2000);
                      logActivity(
                        `Roster tab ${enabled ? 'enabled' : 'disabled'}`,
                        'settings',
                        { setting: 'enableRosterTab', newValue: enabled }
                      );
                    }}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <span className="font-bold text-white">Enable Roster tab (work-in-progress)</span>
                    <div className="text-xs text-gray-400 mt-1">
                      The Roster tab provides a visual character switcher for users with multiple characters. It's currently incomplete and disconnected from other workflows — hidden by default until v1.x finishes the multi-character experience. Opt in to preview the current state. Default OFF.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Keyboard Shortcuts (opt-in) */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-slate-600">
              <h3 className="text-lg font-bold mb-3 text-slate-300 flex items-center gap-2">
                ⌨️ Keyboard shortcuts
              </h3>
              <label className="flex items-start gap-3 text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!settings.keyboardShortcutsEnabled}
                  onChange={(e) => {
                    setSettings(prev => ({ ...prev, keyboardShortcutsEnabled: e.target.checked }));
                    showStatus(e.target.checked ? '⌨️ Keyboard shortcuts enabled' : '⌨️ Keyboard shortcuts disabled', 2000);
                  }}
                  className="mt-1 rounded"
                />
                <span className="text-sm">
                  <span className="font-semibold text-white">Enable single-key shortcuts</span>
                  <span className="block text-gray-400 mt-1">
                    When on, single keys trigger actions: H heal 1 HP, D damage 1 HP, L level-up, E equipment, S spells, I inventory search, R rest, J journal, ? help, Esc close.
                  </span>
                </span>
              </label>
            </div>

            {/* Mobile Interaction Settings */}
            <div className="pt-4 border-t border-gray-700">
              <h3 className="font-bold mb-4 text-amber-400">📱 Mobile Controls</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Touch Interaction Mode</label>
                  <select
                    value={settings.mobileInteractionMode || 'tap'}
                    onChange={(e) => {
                      const newMode = e.target.value;
                      setSettings(prev => {
                        const updated = {
                          ...prev,
                          mobileInteractionMode: newMode
                        };
                        // Force save to localStorage immediately
                        safeLocalStorageSet('tabletopInventory_settings', JSON.stringify(updated));
                        return updated;
                      });
                    }}
                    className="w-full p-2 bg-gray-700 text-white rounded"
                  >
                    <option value="drag">Drag & Drop (Classic)</option>
                    <option value="tap">Tap to Select, Tap to Move</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Current mode: <strong>{settings.mobileInteractionMode || 'tap'}</strong><br/>
                    {(settings.mobileInteractionMode || 'tap') === 'drag'
                      ? "Hold and drag items to move them (traditional)"
                      : "Tap to select (blue highlight), then tap destination to move"}
                  </p>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>• <strong>Long press:</strong> Opens context menu (both modes)</p>
                  <p>• <strong>Double tap:</strong> Edit item or open container</p>
                </div>
              </div>
            </div>

          </div>
        </CollapsibleSection>

        {/* ─── 2. Game Rules ─── */}
        <CollapsibleSection
          title="Game Rules"
          icon={<Dices size={16} />}
          storageKey="settings-game-rules"
          defaultCollapsed={true}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">

            {/* Rule Edition master preset */}
            <div className="space-y-2 bg-indigo-900/20 border border-indigo-500/40 rounded-lg p-3">
              <label className="block text-indigo-200 text-sm font-semibold">
                📖 Rule Edition Preset
              </label>
              <select
                value={settings.ruleEdition || '5e-2014'}
                onChange={(e) => {
                  const newEdition = e.target.value;
                  const oldEdition = settings.ruleEdition || '5e-2014';
                  const presetFields = applyRuleEditionPreset(newEdition);
                  setSettings(prev => ({
                    ...prev,
                    ruleEdition: newEdition,
                    ...presetFields,
                  }));
                  showStatus(
                    newEdition === '5e-2024'
                      ? '📖 Switched to D&D 5e 2024 — exhaustion + bastion defaults updated'
                      : '📖 Switched to D&D 5e 2014 — exhaustion + bastion defaults updated',
                    3000
                  );
                  logActivity(
                    `Rule edition preset switched: ${oldEdition} → ${newEdition}`,
                    'settings',
                    { setting: 'ruleEdition', oldValue: oldEdition, newValue: newEdition, applied: presetFields }
                  );
                }}
                className="w-full p-2 bg-gray-700 text-white rounded border border-indigo-500/30"
              >
                <option value="5e-2014">D&D 5e (2014)</option>
                <option value="5e-2024">D&D 5e (2024 / 5.5e)</option>
              </select>
              <p className="text-xs text-gray-400">
                Sets sensible defaults for the rules below. Individual settings can still be changed independently after picking an edition.
              </p>
            </div>

            {/* Exhaustion Rules */}
            <div className="space-y-2" data-tutorial="settings-rules">
              <label className="block text-gray-300 text-sm font-medium">
                Exhaustion Rules
              </label>
              <select
                value={settings.exhaustionRules || '5e'}
                onChange={(e) => {
                  const newRule = e.target.value;
                  const oldRule = settings.exhaustionRules || '5e';

                  // [UX-3 - NOV 15, 2025]: Fixed NaN appearing in exhaustion rule switch popup
                  // [USER REPORT]: "switching to 5.5 exhaustion the pop up says NaN value" (New Prompts line 23)
                  // [WHY]: NaN is a numeric type, so || 0 doesn't catch it - need explicit isNaN() check
                  // [FIX]: Added proper NaN validation and parsing with fallback to 0

                  // Get current exhaustion with NaN safety
                  let currentExhaustion = timeSystem?.exhaustionLevel;
                  if (currentExhaustion === undefined || currentExhaustion === null || isNaN(currentExhaustion)) {
                    currentExhaustion = 0;
                  } else {
                    currentExhaustion = parseInt(currentExhaustion);
                    if (isNaN(currentExhaustion)) {
                      currentExhaustion = 0;
                    }
                  }

                  const maxForNewRule = newRule === '5e' ? 6 : 10;

                  if (currentExhaustion > maxForNewRule) {
                    const clampedExhaustion = maxForNewRule;
                    setTimeSystem(prev => ({
                      ...prev,
                      exhaustionLevel: clampedExhaustion
                    }));
                    showStatus(
                      `⚠️ Exhaustion clamped from ${currentExhaustion} to ${clampedExhaustion} for ${newRule} rules`,
                      4000
                    );
                    logActivity(
                      `Exhaustion level clamped to ${clampedExhaustion} due to rule change (${oldRule} → ${newRule})`,
                      'character'
                    );
                  } else if (currentExhaustion !== (timeSystem?.exhaustionLevel ?? 0)) {
                    // If we sanitized NaN to 0, update timeSystem
                    setTimeSystem(prev => ({
                      ...prev,
                      exhaustionLevel: currentExhaustion
                    }));
                    showStatus(
                      `⚠️ Invalid exhaustion value detected and reset to ${currentExhaustion}`,
                      4000
                    );
                  }

                  setSettings(prev => ({
                    ...prev,
                    exhaustionRules: newRule
                  }));

                  // [BATCH 13b / P1 D-32 — APR 2026]: Log edition toggle.
                  //   Pre-fix only the side-effect (clamp) was logged when
                  //   exhaustion exceeded the new cap. The toggle itself
                  //   went unlogged. Rule 13 — meaningful action.
                  logActivity(
                    `Exhaustion rules switched: ${oldRule} → ${newRule}`,
                    'settings',
                    { setting: 'exhaustionRules', oldValue: oldRule, newValue: newRule }
                  );

                  // Show rule change confirmation
                  if (currentExhaustion <= maxForNewRule) {
                    showStatus(
                      newRule === '5e'
                        ? '📖 Using D&D 5e (2014) exhaustion rules - 6 levels with specific effects'
                        : '📖 Using D&D 5.5e (2024) exhaustion rules - 10 levels with -N penalty to d20 rolls',
                      3000
                    );
                  }
                }}
                className="w-full p-2 bg-gray-700 text-white rounded"
              >
                <option value="5e">D&D 5e (2014) - 6 Levels</option>
                <option value="5.5e">D&D 5.5e (2024) - 10 Levels</option>
              </select>
              <p className="text-xs text-gray-400">
                {settings.exhaustionRules === '5e'
                  ? "Original D&D 5e rules: disadvantages, speed penalties, HP max halved at level 4"
                  : "Updated 5.5e rules: -1 to -9 penalty on all d20 rolls, death at level 10"}
              </p>
            </div>

            {/* Crafting Rules */}
            <div className="space-y-2">
              <label className="block text-gray-300 text-sm font-medium">
                Crafting Rules
              </label>
              <select
                value={settings.craftingRuleset || 'phb'}
                onChange={(e) => {
                  // [BATCH 13b / P1 D-32 — APR 2026]: Rule 13.
                  const oldRuleset = settings.craftingRuleset || 'phb';
                  const newRuleset = e.target.value;
                  setSettings(prev => ({
                    ...prev,
                    craftingRuleset: newRuleset
                  }));
                  showStatus(
                    newRuleset === 'phb'
                      ? '📖 Using PHB basic crafting rules'
                      : '📖 Using Xanathar\'s crafting rules (helpers, complications)',
                    3000
                  );
                  logActivity(
                    `Crafting ruleset switched: ${oldRuleset} → ${newRuleset}`,
                    'settings',
                    { setting: 'craftingRuleset', oldValue: oldRuleset, newValue: newRuleset }
                  );
                }}
                className="w-full p-2 bg-gray-700 text-white rounded"
              >
                <option value="phb">PHB Basic Rules</option>
                <option value="xgte">Xanathar's Guide (Expanded)</option>
              </select>
              <p className="text-xs text-gray-400">
                {settings.craftingRuleset === 'xgte'
                  ? "XGtE rules: +5gp progress per helper per workweek, 10% complication chance per 5 workweeks"
                  : "Basic crafting: 5gp progress per day, materials cost half item value"}
              </p>
            </div>

            {/* Disable tool requirements checkbox */}
            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.disableCraftingToolRequirements || false}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  disableCraftingToolRequirements: e.target.checked
                }))}
                className="mr-2"
              />
              Disable tool requirements for crafting (DM discretion)
            </label>

            {/* Inspiration System */}
            <div className="bg-gradient-to-br from-purple-900 to-indigo-900 p-4 rounded-lg mb-4 border-2 border-purple-500">
              <h3 className="text-lg font-bold mb-3 text-purple-300 flex items-center gap-2">
                <span>✨</span>
                Inspiration System
              </h3>

              {/* Auto-Prompt Toggle */}
              <label className="flex items-start text-gray-300 mb-4 cursor-pointer hover:bg-purple-800/30 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={settings.inspirationAutoPrompt !== false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      inspirationAutoPrompt: e.target.checked
                    }));
                    showStatus(e.target.checked ?
                      '✨ Inspiration auto-prompt enabled' :
                      '✨ Inspiration auto-prompt disabled', 2000);
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-bold text-white">Enable Auto-Prompt</span>
                  <div className="text-xs text-gray-400 mt-1">
                    Automatically ask to use inspiration when you fail a roll within the threshold
                  </div>
                </div>
              </label>

              {/* Threshold Slider */}
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">
                  <span className="font-bold text-white">Failure Threshold</span>
                  <span className="text-sm text-gray-400 ml-2">(Offer inspiration if failed by ≤ {settings.inspirationThreshold || 3})</span>
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm">1</span>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={settings.inspirationThreshold || 3}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setSettings(prev => ({
                        ...prev,
                        inspirationThreshold: value
                      }));
                    }}
                    className="flex-1 h-2 bg-purple-700 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${((settings.inspirationThreshold || 3) - 1) / 4 * 100}%, #4b5563 ${((settings.inspirationThreshold || 3) - 1) / 4 * 100}%, #4b5563 100%)`
                    }}
                  />
                  <span className="text-gray-400 text-sm">5</span>
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  💡 Example: Threshold of 3 means you'll be prompted if you roll 12 and need 15 (failed by 3)
                </div>
              </div>

              {/* Max Inspiration Limit */}
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">
                  <span className="font-bold text-white">Max Inspiration Points</span>
                  <span className="text-sm text-gray-400 ml-2">(Current: {settings.maxInspiration || 5})</span>
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm">1</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.maxInspiration || 5}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setSettings(prev => ({
                        ...prev,
                        maxInspiration: value
                      }));
                    }}
                    className="flex-1 h-2 bg-purple-700 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${((settings.maxInspiration || 5) - 1) / 9 * 100}%, #4b5563 ${((settings.maxInspiration || 5) - 1) / 9 * 100}%, #4b5563 100%)`
                    }}
                  />
                  <span className="text-gray-400 text-sm">10</span>
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  💡 D&D 5e standard is 1, but you can track multiple points for house rules
                </div>
              </div>

              {/* D&D 5e Rules Reference */}
              <div className="mt-4 bg-indigo-900/30 border border-indigo-500/50 rounded p-3">
                <div className="text-sm text-indigo-300 font-bold mb-1">📖 D&D 5e Rules</div>
                <div className="text-xs text-gray-400 space-y-1">
                  <div>• Inspiration can be spent to reroll ability checks, attack rolls, or saving throws</div>
                  <div>• You must use the new roll, even if it's worse</div>
                  <div>• Standard D&D allows only 1 inspiration point at a time</div>
                  <div>• DM awards inspiration for great roleplay, clever ideas, or heroic actions</div>
                </div>
              </div>
            </div>

            {/* Bastion Settings */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-stone-600">
              <h3 className="text-lg font-bold mb-3 text-stone-300 flex items-center gap-2">
                🏰 Bastion Settings
              </h3>

              <label className="flex items-start text-gray-300 cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={settings.enableBastionUpgrades}
                  onChange={(e) => {
                    // [BATCH 13b / P1 D-32 — APR 2026]: Rule 13.
                    const enabled = e.target.checked;
                    setSettings(prev => ({
                      ...prev,
                      enableBastionUpgrades: enabled
                    }));
                    showStatus(enabled ?
                      '🏰 Facility upgrades enabled (homebrew)' :
                      '🏰 Facility upgrades disabled (DMG 2024 standard)', 2000);
                    logActivity(
                      `Bastion facility upgrades ${enabled ? 'enabled' : 'disabled'} (homebrew)`,
                      'settings',
                      { setting: 'enableBastionUpgrades', newValue: enabled }
                    );
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-bold text-white">Enable Facility Upgrades (Homebrew)</span>
                  <div className="text-xs text-gray-400 mt-1">
                    Allow facilities to be upgraded through 3 levels (Level 1 → 2 → 3). This is a <strong>homebrew rule</strong> not in the official DMG 2024.
                  </div>
                </div>
              </label>

              <label className="flex items-start text-gray-300 cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors mt-2">
                <input
                  type="checkbox"
                  checked={(settings.bastionUnlockLevel || 5) === 1}
                  onChange={(e) => {
                    const newLevel = e.target.checked ? 1 : 5;
                    setSettings(prev => ({
                      ...prev,
                      bastionUnlockLevel: newLevel
                    }));
                    showStatus(e.target.checked ?
                      '🏰 Bastions unlock at Level 1 (homebrew)' :
                      '🏰 Bastions unlock at Level 5 (D&D 2024 rules)', 2000);
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-bold text-white">Unlock Bastion at Level 1 (Homebrew)</span>
                  <div className="text-xs text-gray-400 mt-1">
                    By default, bastions unlock at Level 5 per D&D 2024 DMG rules. Enable this to unlock at Level 1.
                  </div>
                </div>
              </label>

              <div className="mt-3 bg-stone-900/50 border border-stone-700/50 rounded p-3">
                <div className="text-xs text-stone-300">
                  <strong>📖 DMG 2024 Rules:</strong> Bastions unlock at Level 5. Facilities don't have upgrade levels — you either have a facility or you don't. The options above allow homebrew customization.
                </div>
              </div>
            </div>

            {/* Armor Proficiency Penalties */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-stone-600">
              <h3 className="text-lg font-bold mb-3 text-stone-300 flex items-center gap-2">
                🛡️ Armor Rules
              </h3>

              <label className="flex items-start text-gray-300 cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={settings.enableArmorProficiencyPenalties || false}
                  onChange={(e) => {
                    // [Phase B #323 — MAY 2026]: Rule 13.
                    const enabled = e.target.checked;
                    setSettings(prev => ({
                      ...prev,
                      enableArmorProficiencyPenalties: enabled
                    }));
                    showStatus(enabled ?
                      '🛡️ Armor proficiency penalties enabled (PHB p.144)' :
                      '🛡️ Armor proficiency penalties disabled', 2000);
                    logActivity(
                      `Armor proficiency penalties ${enabled ? 'enabled' : 'disabled'} (PHB p.144)`,
                      'settings',
                      { setting: 'enableArmorProficiencyPenalties', newValue: enabled }
                    );
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-bold text-white">PHB p.144 armor-proficiency penalties</span>
                  <div className="text-xs text-gray-400 mt-1">
                    When enabled, wearing armor your character is NOT proficient with imposes disadvantage on STR/DEX checks, saves, and attack rolls — and you cannot cast spells. Default OFF for v1.0.x compatibility.
                  </div>
                </div>
              </label>

              <div className="mt-3 bg-stone-900/50 border border-stone-700/50 rounded p-3">
                <div className="text-xs text-stone-300">
                  <strong>📖 PHB p.144 Rules:</strong> If you wear armor you lack proficiency with, you have disadvantage on ability checks, saving throws, and attack rolls that involve Strength or Dexterity, and you can't cast spells.
                </div>
              </div>
            </div>

            {/* Encounter Difficulty — A50a (2026-05-25 session 8c1af7ca) */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-stone-600">
              <h3 className="text-lg font-bold mb-3 text-stone-300 flex items-center gap-2">
                ⚔️ Encounter Difficulty
              </h3>

              <label className="flex items-start text-gray-300 cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={settings.enableCPIDrawer || false}
                  onChange={(e) => {
                    // [A50a — 2026-05-25 session 8c1af7ca]: CPI/SPI drawer toggle.
                    //   Pre-fix: enableCPIDrawer existed in SettingsContext.js:72
                    //   with default false; CombatTab.js:192 + MonsterEditor.js:186
                    //   both read it correctly; this Settings UI had no row to flip
                    //   the flag, so the CPI drawer was permanently hidden from
                    //   users. Source: docs/audits/master-queue.md Wave-3
                    //   transcript-dive A50a + .claude/findings/2026-05-24-orchestration-transcript-triage.md.
                    const enabled = e.target.checked;
                    setSettings(prev => ({
                      ...prev,
                      enableCPIDrawer: enabled
                    }));
                    showStatus(enabled ?
                      '⚔️ CPI/SPI drawer enabled (alternate difficulty overlay)' :
                      '⚔️ CPI/SPI drawer disabled (DMG XP-budget RAW only)', 2000);
                    logActivity(
                      `CPI/SPI drawer ${enabled ? 'enabled' : 'disabled'}`,
                      'settings',
                      { setting: 'enableCPIDrawer', newValue: enabled }
                    );
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-bold text-white">Show CPI/SPI drawer in Combat Manager + Monster Editor</span>
                  <div className="text-xs text-gray-400 mt-1">
                    When enabled, the cross-system Combat Power Index (CPI, 14-axis) and Strategic Power Index (SPI, 6-dimension) overlay renders alongside the DMG XP-budget RAW difficulty band in Combat Manager, and a CPI drawer surfaces in the Monster Editor. The DMG XP-budget band remains the default primary readout; CPI/SPI is an opt-in alternate for groups using bespoke or multi-system metrics. Default OFF.
                  </div>
                </div>
              </label>

              <div className="mt-3 bg-stone-900/50 border border-stone-700/50 rounded p-3">
                <div className="text-xs text-stone-300">
                  <strong>📖 DMG XP-budget Default:</strong> Difficulty bands are computed from party levels × DMG 2014 p.82-83 XP thresholds × encounter multipliers (DMG p.82). CPI/SPI is an opt-in cross-system overlay imported from BibleBuilder; it does not replace the RAW path.
                </div>
              </div>
            </div>

            {/* Off-List Scroll Casting — Sub-plan 4M (2026-05-28) */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-stone-600">
              <h3 className="text-lg font-bold mb-3 text-stone-300 flex items-center gap-2">
                📜 Scroll Rules
              </h3>

              <label className="flex items-start text-gray-300 cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={settings.enableOffListCasting || false}
                  onChange={(e) => {
                    // [Sub-plan 4M — 2026-05-28]: Off-list scroll casting toggle. Rule 13.
                    const enabled = e.target.checked;
                    setSettings(prev => ({
                      ...prev,
                      enableOffListCasting: enabled
                    }));
                    showStatus(enabled ?
                      '📜 Off-list scroll casting enabled (Arcana DC 10 + spell level)' :
                      '📜 Off-list scroll casting disabled (RAW — scroll unintelligible)', 2000);
                    logActivity(
                      `Off-list scroll casting ${enabled ? 'enabled' : 'disabled (RAW)'}`,
                      'settings',
                      { setting: 'enableOffListCasting', newValue: enabled }
                    );
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-bold text-white">Allow Off-Class-List Scroll Casting (Arcana DC 10 + spell level)</span>
                  <div className="text-xs text-gray-400 mt-1">
                    House rule per DMG p.139 — RAW the scroll is unintelligible if the spell is not on your class list. When enabled, attempting to read an off-list scroll triggers an Intelligence (Arcana) check; failure fizzles the spell and consumes the scroll. Default OFF (RAW).
                  </div>
                </div>
              </label>

              <div className="mt-3 bg-stone-900/50 border border-stone-700/50 rounded p-3">
                <div className="text-xs text-stone-300">
                  <strong>📖 DMG p.139 RAW:</strong> "If the spell is on your class's spell list, you can read the scroll and cast its spell... Otherwise, the scroll is unintelligible." The Arcana check option is a house rule — this toggle defaults OFF to preserve RAW behavior.
                </div>
              </div>
            </div>

            {/* Equipment Management Settings */}
            <div className="pt-4 border-t border-gray-700">
              <h3 className="font-bold mb-4 text-amber-400">⚔️ Equipment Management</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Equipment Slot Mode</label>
                  <select
                    value={settings.equipmentSlotMode}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      equipmentSlotMode: e.target.value
                    }))}
                    className="w-full p-2 bg-gray-700 text-white rounded"
                  >
                    <option value="simplified">Simplified (5 slots)</option>
                    <option value="standard">Standard D&D (13 slots)</option>
                    <option value="realistic">Realistic RPG (20 slots)</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    {settings.equipmentSlotMode === 'simplified' && "Basic slots for quick gameplay"}
                    {settings.equipmentSlotMode === 'standard' && "Traditional D&D equipment slots"}
                    {settings.equipmentSlotMode === 'realistic' && "Detailed slots for immersive gameplay"}
                  </p>
                </div>

                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={settings.showEquipmentWeight}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      showEquipmentWeight: e.target.checked
                    }))}
                    className="mr-2"
                  />
                  Show equipment weight in slots
                </label>

                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={settings.enforceSlotLimits}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      enforceSlotLimits: e.target.checked
                    }))}
                    className="mr-2"
                  />
                  Enforce slot restrictions (prevent invalid equips)
                </label>

                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={settings.quickEquipEnabled}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      quickEquipEnabled: e.target.checked
                    }))}
                    className="mr-2"
                  />
                  Enable quick equip [+] buttons
                </label>

                <label className="flex items-center text-gray-300 opacity-50">
                  <input
                    type="checkbox"
                    checked={settings.visualEquipmentDisplay}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      visualEquipmentDisplay: e.target.checked
                    }))}
                    className="mr-2"
                    disabled
                  />
                  Visual equipment display (coming soon)
                </label>
              </div>
            </div>

            {/* Inventory Options */}
            <div>
              <label className="block text-sm text-gray-300 mb-1">
                Default Sell Ratio (% of item value)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={Math.round(settings.defaultSellRatio * 100)}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    defaultSellRatio: (parseInt(e.target.value) || 50) / 100
                  }))}
                  className="w-32 p-2 bg-gray-700 text-white rounded"
                />
                <span className="text-gray-400">%</span>
              </div>
            </div>

            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.currencyHasWeight}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  currencyHasWeight: e.target.checked
                }))}
                className="mr-2"
              />
              Currency has weight (50 coins = 1 lb)
            </label>

            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.allowContainersInContainers}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  allowContainersInContainers: e.target.checked
                }))}
                className="mr-2"
              />
              Allow containers inside containers (follows size hierarchy)
            </label>

            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.autoOpenContainers}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  autoOpenContainers: e.target.checked
                }))}
                className="mr-2"
              />
              Auto-open containers when items are dropped in
            </label>

            {settings.displayMode !== DISPLAY_MODES.JUST_INVENTORY && (
              <label className="flex items-center text-gray-300 bg-purple-900 bg-opacity-30 p-3 rounded-lg border border-purple-600">
                <input
                  type="checkbox"
                  checked={settings.trackLowCostComponents || false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      trackLowCostComponents: e.target.checked
                    }));
                    showStatus(e.target.checked ?
                      '🧪 Manual component tracking enabled - Stock up on bat guano!' :
                      '📿 Component pouch handles basic materials automatically', 3000);
                  }}
                  className="mr-3"
                />
                <div>
                  <span className="font-bold">Track Low-Cost Components Manually</span>
                  <div className="text-xs text-gray-400">
                    Require specific materials (bat guano, sulfur, etc.) even for components under 1gp.
                    When disabled, component pouch or arcane focus covers all non-costly materials.
                  </div>
                </div>
              </label>
            )}

            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.autoPurchaseItems}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  autoPurchaseItems: e.target.checked
                }))}
                className="mr-2"
              />
              Auto-purchase items when adding to inventory (deduct gold cost)
            </label>

            {settings.autoPurchaseItems && (
              <label className="flex items-center text-gray-300 ml-6">
                <input
                  type="checkbox"
                  checked={settings.useVaultGoldForPurchases}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    useVaultGoldForPurchases: e.target.checked
                  }))}
                  className="mr-2"
                />
                Also use gold from vaults for purchases
              </label>
            )}

            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.rememberLastCharacter}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  rememberLastCharacter: e.target.checked
                }))}
                className="mr-2"
              />
              Remember last character and auto-load on startup
            </label>

            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.classDependentSpellbook}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  classDependentSpellbook: e.target.checked
                }))}
                className="mr-2"
              />
              Show only class-specific spells in spellbook
            </label>

            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={settings.enforceComponentConsumption !== false}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  enforceComponentConsumption: e.target.checked
                }))}
                className="mr-2"
              />
              Enforce component consumption rules (consume materials on cast)
            </label>

          </div>
        </CollapsibleSection>

        {/* ─── 3. Content Workshop ─── */}
        <CollapsibleSection
          title="Content Workshop"
          icon={<Book size={16} />}
          storageKey="settings-content-workshop"
          defaultCollapsed={true}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">
            {/* [APR 2026 RE-ADDRESS]: ContentWorkshopSection is character-build
                tooling — DEFAULT only. SIMPLE/JI players see a hint that
                explains the gate and how to enable it. SourcesPanel stays
                visible (read-only browse + toggle source visibility) since
                that's content management, not authoring. */}
            <div data-tutorial="settings-workshop">
              {settings.displayMode === DISPLAY_MODES.DEFAULT ? (
                <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4 text-sm">
                  <div className="font-bold text-emerald-300 mb-1">⚒️ Content Workshop has its own tab</div>
                  <div className="text-gray-400">
                    Create custom races, classes, subclasses, and spells from the <strong className="text-emerald-300">Workshop</strong> tab in the navigation.
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 text-sm text-gray-400">
                  <strong className="text-gray-300">Content Workshop</strong> is only available in Default View.
                  Switch to Default View above to create custom races, classes, subclasses, and spells.
                </div>
              )}
            </div>

            <div className="mb-6">
              <SourcesPanel showStatus={showStatus} />
            </div>
          </div>
        </CollapsibleSection>

        {/* ─── 3b. Image Library ─── (Spec D Task 25-28 / 2026-05-17) */}
        <CollapsibleSection
          title="Image Library"
          icon={<ImageIcon size={16} />}
          storageKey="settings-image-library"
          defaultCollapsed={true}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <ImageLibrarySection
            playerStats={playerStats}
            setPlayerStats={setPlayerStats}
            showStatus={showStatus}
          />
        </CollapsibleSection>

        {/* ─── 3c. Accessibility ─── [A11Y — 2026-06-20] */}
        {/* [WHY]: Opt-in overlay — default OFF, zero visual change until toggled.
            Toggles write to settings.accessibility; AppearanceController converts
            them to data-a11y tokens on <html>; themes.css rules fire from there.
            No existing component JSX is touched. */}
        <CollapsibleSection
          title="Accessibility"
          icon={<Eye size={16} />}
          storageKey="settings-accessibility"
          defaultCollapsed={true}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">
            <div className="bg-indigo-900/20 border border-indigo-700/50 rounded-lg p-4">
              <h4 className="font-bold text-indigo-200 mb-1">Accessibility Options</h4>
              <p className="text-xs text-gray-400 mb-3">
                These settings apply a visual overlay on top of the existing theme.
                All options are off by default and can be combined freely.
              </p>

              {/* Dyslexia-friendly font */}
              <label className="flex items-center text-gray-300 mb-3">
                <input
                  type="checkbox"
                  checked={!!(settings.accessibility && settings.accessibility.dyslexiaFont)}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      accessibility: {
                        ...(prev.accessibility || {}),
                        dyslexiaFont: e.target.checked
                      }
                    }));
                  }}
                  className="mr-3"
                  data-testid="a11y-dyslexia-font"
                />
                <div>
                  <span className="font-bold text-white">Dyslexia-friendly font</span>
                  <div className="text-xs text-gray-400">
                    Switches to a wider, clearer font stack (Atkinson Hyperlegible / Verdana)
                    with extra line, letter, and word spacing.
                  </div>
                </div>
              </label>

              {/* Large text */}
              <label className="flex items-center text-gray-300 mb-3">
                <input
                  type="checkbox"
                  checked={!!(settings.accessibility && settings.accessibility.largeText)}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      accessibility: {
                        ...(prev.accessibility || {}),
                        largeText: e.target.checked
                      }
                    }));
                  }}
                  className="mr-3"
                  data-testid="a11y-large-text"
                />
                <div>
                  <span className="font-bold text-white">Larger text</span>
                  <div className="text-xs text-gray-400">
                    Increases the base font size (~12% larger). All spacing scales with it.
                  </div>
                </div>
              </label>

              {/* High contrast */}
              <label className="flex items-center text-gray-300 mb-3">
                <input
                  type="checkbox"
                  checked={!!(settings.accessibility && settings.accessibility.highContrast)}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      accessibility: {
                        ...(prev.accessibility || {}),
                        highContrast: e.target.checked
                      }
                    }));
                  }}
                  className="mr-3"
                  data-testid="a11y-high-contrast"
                />
                <div>
                  <span className="font-bold text-white">Higher contrast</span>
                  <div className="text-xs text-gray-400">
                    Brightens muted text and strengthens borders for users with low vision.
                  </div>
                </div>
              </label>

              {/* Reduced motion */}
              <label className="flex items-center text-gray-300">
                <input
                  type="checkbox"
                  checked={!!(settings.accessibility && settings.accessibility.reducedMotion)}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      accessibility: {
                        ...(prev.accessibility || {}),
                        reducedMotion: e.target.checked
                      }
                    }));
                  }}
                  className="mr-3"
                  data-testid="a11y-reduced-motion"
                />
                <div>
                  <span className="font-bold text-white">Reduce motion</span>
                  <div className="text-xs text-gray-400">
                    Disables animations and transitions. Also respects your OS-level
                    "prefers reduced motion" setting automatically.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </CollapsibleSection>

        {/* ─── 3d. Theme ─── [DESIGN-THEME-SUBSTRATE 2026-07-04] */}
        {/* [WHY]: Picker + registry only — zero pre-authored theme content ships
            in this arc. Selecting writes settings.theme; AppearanceController
            (already wired) sets/removes data-theme on <html>. New themes arrive
            later via Claude Design bring-backs per the contract documented in
            src/utils/themeRegistry.js — this section never needs to change to
            support them, only AVAILABLE_THEMES grows. */}
        <CollapsibleSection
          title="Theme"
          icon={<Palette size={16} />}
          storageKey="settings-theme"
          defaultCollapsed={true}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">
            <div className="bg-indigo-900/20 border border-indigo-700/50 rounded-lg p-4">
              <h4 className="font-bold text-indigo-200 mb-1">Theme Options</h4>
              <p className="text-xs text-gray-400 mb-3">
                Themes are additive and instantly reversible — switching back to
                Default never loses anything. New themes arrive over time via
                Claude Design bring-backs. Default is the current look.
              </p>

              <label className="flex flex-col text-gray-300">
                <span className="font-bold text-white mb-1">Theme</span>
                <select
                  data-testid="theme-picker-select"
                  value={settings.theme || ''}
                  onChange={(e) => {
                    const nextTheme = e.target.value;
                    setSettings(prev => ({
                      ...prev,
                      theme: nextTheme
                    }));
                  }}
                  className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
                >
                  {AVAILABLE_THEMES.map(themeOption => (
                    <option key={themeOption.id} value={themeOption.id}>
                      {themeOption.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </CollapsibleSection>

        {/* ─── 4. Account ─── */}
        <CollapsibleSection
          title="Account"
          icon={<Lock size={16} />}
          storageKey="settings-account"
          defaultCollapsed={false}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">
            <AccountPanel />

            {/* [APR18-FIX]: Manual premium-status refresh — escape hatch for the Stripe
                webhook race. If user pays but the webhook hasn't flipped isPremium yet,
                this forces a re-read from Firebase. */}
            {isAuthenticated && (
              <div className="bg-gray-800 p-3 rounded-lg border border-gray-700 flex items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="font-medium text-white">Premium status</div>
                  <div className="text-xs text-gray-400">
                    {isPremium ? 'Active — thank you!' : 'Not detected. If you purchased, click refresh.'}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const result = await refreshPremiumStatus();
                    if (!result) {
                      showStatus('Refresh failed — check your connection.', 3000);
                    } else if (result.isPremium) {
                      showStatus('✨ Premium active.', 3000);
                    } else {
                      showStatus('Still no premium found. If you just paid, wait ~10s and try again.', 5000);
                    }
                  }}
                  className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
                >
                  Refresh
                </button>
              </div>
            )}

            {/* [S5]: Delete Account — typed confirmation + re-auth support */}
            {isAuthenticated && (
              <div className="bg-gray-800 p-4 rounded-lg border border-red-900/50">
                <button
                  onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(''); setDeleteError(''); setShowReauthPrompt(false); setReauthPassword(''); }}
                  className="w-full flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <Trash2 size={18} className="text-red-400" />
                    <div className="text-left">
                      <div className="font-semibold text-red-300 group-hover:text-red-200 transition-colors">Delete Account</div>
                      <div className="text-xs text-gray-500">Permanently remove account + all cloud data</div>
                    </div>
                  </div>
                  <AlertTriangle size={16} className="text-red-500/50" />
                </button>
              </div>
            )}

            {/* [S5]: Delete Account Confirmation Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4" style={{ zIndex: 165 }}>
                <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-red-800 overflow-hidden">
                  <div className="bg-gradient-to-r from-red-900 to-red-800 p-5">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <AlertTriangle size={20} />
                      Delete Account
                    </h2>
                    <p className="text-sm text-red-200 mt-1">This action is permanent and cannot be undone.</p>
                  </div>

                  <div className="p-5 space-y-4">
                    {deleteError && (
                      <div className="p-3 bg-red-900/30 border border-red-600/50 rounded-lg text-sm text-red-300">
                        {deleteError}
                      </div>
                    )}

                    <div className="bg-gray-700/50 rounded-lg p-4 text-sm text-gray-300 space-y-2">
                      <p>This will:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-400">
                        <li>Download a copy of all your data first</li>
                        <li>Delete your account and authentication credentials</li>
                        <li>Remove all cloud-stored data (characters, settings, purchases)</li>
                        <li>Remove you from any parties</li>
                        <li>Clear all local app data</li>
                      </ul>
                      <p className="text-amber-400 text-xs mt-2">Premium purchases and AI tokens are non-recoverable after deletion.</p>
                    </div>

                    {!showReauthPrompt ? (
                      <>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            Type <code className="bg-gray-700 px-1.5 py-0.5 rounded text-red-300 font-mono">DELETE MY ACCOUNT</code> to confirm
                          </label>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE MY ACCOUNT"
                            className="w-full p-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono"
                            autoComplete="off"
                          />
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowDeleteModal(false)}
                            className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg font-medium transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleDeleteAccount}
                            disabled={deleteConfirmText !== 'DELETE MY ACCOUNT' || deleteLoading}
                            className="flex-1 py-2.5 bg-red-700 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {deleteLoading ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <><Trash2 size={14} /> Delete Forever</>
                            )}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Re-authentication prompt for email/password users */}
                        <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
                          <p className="text-sm text-amber-300">For security, please re-enter your password to confirm deletion.</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Password</label>
                          <input
                            type="password"
                            value={reauthPassword}
                            onChange={(e) => setReauthPassword(e.target.value)}
                            placeholder="Enter your password"
                            className="w-full p-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          />
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => { setShowDeleteModal(false); setShowReauthPrompt(false); }}
                            className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg font-medium transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleReauthAndDelete}
                            disabled={!reauthPassword || deleteLoading}
                            className="flex-1 py-2.5 bg-red-700 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {deleteLoading ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              'Confirm & Delete'
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* [S5]: Export all my data — GDPR/CCPA-compliant data export */}
            {isAuthenticated && (
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <button
                  onClick={handleExportAllData}
                  disabled={exportLoading}
                  className="w-full flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <Download size={18} className="text-blue-400" />
                    <div className="text-left">
                      <div className="font-semibold text-gray-300 group-hover:text-white transition-colors">Export all my data</div>
                      <div className="text-xs text-gray-500">Download all characters, settings, and account data as JSON</div>
                    </div>
                  </div>
                  {exportLoading ? (
                    <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                  ) : (
                    <Download size={14} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
                  )}
                </button>
              </div>
            )}
          </div>
        </CollapsibleSection>


        {/* ─── 6. Data Management ─── */}
        <CollapsibleSection
          title="Data Management"
          icon={<Save size={16} />}
          storageKey="settings-data-management"
          defaultCollapsed={true}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">

            {/* Tutorial boxes hide toggle */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-cyan-600">
              <h3 className="text-lg font-bold mb-3 text-cyan-300 flex items-center gap-2">
                <Info size={20} />
                Tutorial & Info Boxes
              </h3>

              <label className="flex items-start text-gray-300 cursor-pointer hover:bg-gray-700/50 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={settings.hideTutorials}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      hideTutorials: e.target.checked
                    }));
                    showStatus(e.target.checked ?
                      '📚 Tutorial boxes hidden' :
                      '📚 Tutorial boxes visible', 2000);
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-bold text-white">Hide All Tutorial Boxes</span>
                  <div className="text-xs text-gray-400 mt-1">
                    Hide the blue info boxes that explain features. Useful once you've learned the system.
                  </div>
                </div>
              </label>

              {/* [TUTORIAL-1]: Browse / Replay individual tours */}
              <div className="mt-3">
                <button
                  onClick={() => setShowTutorialHelpMenu(true)}
                  className="w-full px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  data-tutorial="settings-browse-tours"
                >
                  <BookOpen size={16} />
                  Browse / Replay Tours
                </button>
                <div className="text-xs text-gray-400 mt-1.5 text-center">
                  Re-watch any individual guided tour without losing your progress.
                </div>
              </div>

              {/* Reset All Tours button (was "Replay Guided Tutorials" — relabeled TUTORIAL-1) */}
              <div className="mt-3">
                <button
                  onClick={() => {
                    resetAllTutorials();
                    showStatus('All tours reset! The welcome guide will appear again.', 3000);
                  }}
                  className="w-full px-4 py-2 bg-purple-700 hover:bg-purple-600 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  data-tutorial="settings-replay-tutorials"
                >
                  <Sparkles size={16} />
                  Reset All Tours
                </button>
                <div className="text-xs text-gray-400 mt-1.5 text-center">
                  Wipe all tour progress and show the welcome screen again.
                </div>
              </div>

              <div className="mt-3 bg-blue-900/20 border border-blue-700/50 rounded p-3">
                <div className="text-xs text-blue-200">
                  <strong>💡 Tip:</strong> Some tutorial boxes can also be dismissed individually by clicking the X button.
                  They won't reappear until you clear your browser data.
                </div>
              </div>
            </div>

            {/* Autosave Interval */}
            <div className="space-y-2">
              <label className="block text-gray-300 text-sm font-medium">
                Autosave Interval
              </label>
              <select
                value={settings.autosaveInterval}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  autosaveInterval: parseInt(e.target.value)
                }))}
                className="w-full p-2 bg-gray-700 text-white rounded"
              >
                <option value={0}>Disabled</option>
                <option value={30000}>Every 30 seconds</option>
                <option value={60000}>Every 1 minute</option>
                <option value={300000}>Every 5 minutes</option>
                <option value={600000}>Every 10 minutes</option>
              </select>
            </div>

            {/* Autosave Status */}
            <div className="bg-blue-900/50 p-4 rounded border border-blue-600">
              <h4 className="font-medium mb-2 text-blue-200">🔄 Autosave Status</h4>
              <div className="text-sm text-blue-300 space-y-1 mb-3">
                {/* [AUTOSAVE-DEBOUNCE-STARVATION — 2026-07-18]: this line used to
                    hardcode "every 30 seconds" while the code was a 2s debounce that
                    sustained activity could starve indefinitely. The interval setting
                    is now the real worst-case cadence (max-wait), so describe the
                    actual behavior instead of a fixed number. */}
                <div>✓ Saves a moment after you make changes — never longer than your chosen interval</div>
                <div>✓ Saves when you close/refresh the page</div>
                <div>✓ Restores your session when you return</div>
              </div>
              <button
                onClick={clearAutosave}
                className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors"
              >
                Clear Autosave Data
              </button>
            </div>

            {/* Current Character Save */}
            <div className="bg-gray-700 p-4 rounded">
              <h4 className="font-medium mb-2 text-white">Current Character</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => saveCharacter()}
                  className="bg-blue-700 hover:bg-blue-600 px-3 py-1 rounded text-sm transition-colors"
                >
                  <Save size={14} className="inline mr-1" />
                  Quick Save
                </button>
                <div className="relative inline-block">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        exportCharacter(null, e.target.value);
                        e.target.value = ''; // Reset
                      }
                    }}
                    className="bg-green-700 hover:bg-green-600 px-3 py-1 rounded text-sm transition-colors appearance-none cursor-pointer text-white border-none"
                    value=""
                  >
                    <option value="">📤 Export As...</option>
                    <option value="json">📋 JSON (Full Data)</option>
                    <option value="txt">📝 Text (Character Sheet)</option>
                    <option value="html">🌐 HTML (Pretty Print)</option>
                    <option value="csv">📊 CSV (Spreadsheet)</option>
                  </select>
                </div>
                <label className="bg-yellow-700 hover:bg-yellow-600 px-3 py-1 rounded text-sm transition-colors cursor-pointer">
                  <Upload size={14} className="inline mr-1" />
                  Import
                  <input
                    type="file"
                    accept=".json"
                    onChange={importInventory}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Saved Characters List */}
            {savedCharacters.length > 0 && (
              <div className="bg-gray-700 p-4 rounded">
                <h4 className="font-medium mb-3 text-white">Saved Characters ({savedCharacters.length})</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {savedCharacters.map(character => (
                    <div key={character.id} className="bg-gray-800 p-3 rounded flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-white">{character.name}</div>
                        <div className="text-xs text-gray-400">
                          Level {character.data.playerStats.level} {character.data.playerStats.classes?.[0]?.className || character.data.playerStats.class || 'Adventurer'} |
                          Saved: {new Date(character.timestamp).toLocaleDateString()} {new Date(character.timestamp).toLocaleTimeString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {character.data.inventory.length} items, {character.data.vaults.length} vaults
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => loadCharacter(character.id)}
                          className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded text-xs transition-colors"
                          title="Load Character"
                        >
                          📂
                        </button>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              exportCharacter(character.id, e.target.value);
                              e.target.value = '';
                            }
                          }}
                          className="bg-green-600 hover:bg-green-700 px-2 py-1 rounded text-xs transition-colors appearance-none cursor-pointer text-white border-none"
                          title="Export Character"
                          value=""
                        >
                          <option value="">📤</option>
                          <option value="json">JSON</option>
                          <option value="txt">Text</option>
                          <option value="html">HTML</option>
                          <option value="csv">CSV</option>
                        </select>
                        <button
                          onClick={() => duplicateCharacter(character.id)}
                          className="bg-purple-600 hover:bg-purple-700 px-2 py-1 rounded text-xs transition-colors"
                          title="Duplicate Character"
                        >
                          📋
                        </button>
                        <button
                          onClick={() => deleteCharacter(character.id)}
                          className="bg-red-600 hover:bg-red-700 px-2 py-1 rounded text-xs transition-colors"
                          title="Delete Character"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reset all settings */}
            <div className="pt-4 border-t border-gray-700">
              <h3 className="font-bold mb-3 text-red-300">Reset</h3>
              <button
                onClick={async () => {
                  if (await confirm({ title: 'Reset All Settings', message: 'This will reset all settings to defaults. Your characters and inventory are NOT affected.', confirmText: 'Reset', confirmColor: 'red' })) {
                    resetSettings();
                    showStatus('⚙️ All settings reset to defaults', 3000);
                  }
                }}
                className="w-full bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded transition-colors text-sm font-medium"
              >
                Reset all settings to defaults
              </button>
            </div>

            {/* [S5]: Download all my data — full export with schema version */}
            {isAuthenticated && (
              <div className="bg-gray-800 p-4 rounded-lg border border-blue-900/50">
                <button
                  onClick={handleExportAllData}
                  disabled={exportLoading}
                  className="w-full flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <Download size={18} className="text-blue-400" />
                    <div className="text-left">
                      <div className="font-semibold text-gray-300 group-hover:text-white transition-colors">Download all my data</div>
                      <div className="text-xs text-gray-500">Full export: characters, settings, custom content, account data</div>
                    </div>
                  </div>
                  {exportLoading ? (
                    <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                  ) : (
                    <ExternalLink size={14} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2 ml-8">
                  GDPR/CCPA compliant. Exports as JSON with schema versioning.
                </p>
              </div>
            )}

          </div>
        </CollapsibleSection>

        {/* ─── 7. About ─── */}
        <CollapsibleSection
          title="About"
          icon={<Info size={16} />}
          storageKey="settings-about"
          defaultCollapsed={true}
          headerClassName="bg-gray-700/50 rounded-lg"
        >
          <div className="space-y-4 mt-2">
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <h3 className="text-lg font-bold mb-2 text-white">Tabletop Inventory Manager</h3>
              <div className="text-sm text-gray-400 space-y-1">
                <div>Version <span className="font-mono text-gray-300">v{APP_VERSION}</span></div>
                <div>Build date: <span className="font-mono text-gray-300">{new Date(document.lastModified).toLocaleDateString()}</span></div>
              </div>
            </div>

            {/* [Sub-plan 4A]: Download Debug Report — bundles character state,
                  IDB health, localStorage usage, and browser env in one JSON.
                  Useful when reporting bugs to support. Rule 13: logActivity
                  is called inside exportDebugBundle in App.jsx. */}
            {exportDebugBundle && (
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <button
                  onClick={exportDebugBundle}
                  className="w-full flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <Download size={18} className="text-cyan-400" />
                    <div className="text-left">
                      <div className="font-semibold text-gray-300 group-hover:text-white transition-colors">Download Debug Report</div>
                      <div className="text-xs text-gray-500">Character snapshot + storage health + browser info — share when reporting bugs</div>
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-gray-500 group-hover:text-cyan-400 transition-colors" />
                </button>
              </div>
            )}

            {/* [S5]: OGL / WotC Attribution — collapsible legal notice */}
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <button
                onClick={() => setShowOglContent(!showOglContent)}
                className="w-full flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-amber-400" />
                  <div className="text-left">
                    <div className="font-semibold text-gray-300 group-hover:text-white transition-colors">OGL / WotC Attribution</div>
                    <div className="text-xs text-gray-500">Legal notice for D&D 5e SRD content</div>
                  </div>
                </div>
                {showOglContent ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
              </button>

              {showOglContent && (
                <div className="mt-4 space-y-4 text-sm text-gray-400 border-t border-gray-700 pt-4">
                  {/* SRD 5.1 Content */}
                  <div>
                    <h4 className="font-bold text-amber-300 mb-2">SRD 5.1 Content</h4>
                    <p className="mb-2">
                      This app includes content from the Systems Reference Document 5.1 ("SRD 5.1")
                      by Wizards of the Coast LLC, used under the{' '}
                      <a href="https://creativecommons.org/licenses/by/4.0/legalcode" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                        Creative Commons Attribution 4.0 International License
                      </a>.
                    </p>
                    <div className="bg-gray-700/50 rounded p-3 text-xs space-y-1">
                      <div>Race names, traits, and ability score bonuses</div>
                      <div>Class names, features, hit dice, and progression tables</div>
                      <div>Subclass names and features</div>
                      <div>343 spell names, descriptions, and mechanics</div>
                      <div>42 feat names and mechanical effects</div>
                      <div>Equipment names, properties, costs, and weights</div>
                      <div>13 background names and features</div>
                      <div>Ability scores, skills, conditions, and game mechanics</div>
                    </div>
                  </div>

                  {/* Attribution */}
                  <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3">
                    <p className="text-xs text-amber-200">
                      This product includes content from the SRD 5.1, copyright 2016 Wizards of the Coast LLC.
                      Licensed under the Creative Commons Attribution 4.0 International License.
                    </p>
                  </div>

                  {/* Trademark Notice */}
                  <div>
                    <h4 className="font-bold text-gray-300 mb-2">Trademark Notice</h4>
                    <ul className="text-xs space-y-1.5 text-gray-500">
                      <li>"Dungeons & Dragons", "D&D", "Wizards of the Coast", and their logos are trademarks of Wizards of the Coast LLC.</li>
                      <li>Tabletop Inventory Manager is not affiliated with, endorsed by, or sponsored by Wizards of the Coast LLC.</li>
                      <li>This is an independent product using SRD 5.1 content under CC BY 4.0.</li>
                    </ul>
                  </div>

                  {/* Third-Party Content */}
                  <div>
                    <h4 className="font-bold text-gray-300 mb-2">Third-Party Expansion Content</h4>
                    <p className="text-xs text-gray-500 mb-2">
                      Content from the following sourcebooks is included as optional toggleable sources.
                      Mechanical implementations are based on the SRD and publicly available rule summaries.
                      No copyrighted text is reproduced verbatim.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-0.5 ml-3">
                      <li>Tasha's Cauldron of Everything (2020)</li>
                      <li>Xanathar's Guide to Everything (2017)</li>
                      <li>Fizban's Treasury of Dragons (2021)</li>
                      <li>Volo's Guide to Monsters (2016)</li>
                    </ul>
                  </div>

                  {/* Fan Content Statement */}
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-400">
                      <strong className="text-gray-300">Fan Content Policy:</strong>{' '}
                      This application is unofficial Fan Content permitted under the{' '}
                      <a href="https://company.wizards.com/en/legal/fancontentpolicy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                        Wizards of the Coast Fan Content Policy
                      </a>.
                      Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Privacy / ToS / Refund / Support — live links wired in Session 7 */}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-gray-800 p-4 rounded-lg border border-gray-700 hover:border-blue-500 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock size={18} className="text-blue-400" />
                  <div>
                    <div className="font-semibold text-white">Privacy Policy</div>
                    <div className="text-xs text-gray-400">How we collect and protect your data</div>
                  </div>
                </div>
                <ExternalLink size={16} className="text-gray-400" />
              </div>
            </a>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-gray-800 p-4 rounded-lg border border-gray-700 hover:border-blue-500 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-blue-400" />
                  <div>
                    <div className="font-semibold text-white">Terms of Service</div>
                    <div className="text-xs text-gray-400">Account, premium, and acceptable use</div>
                  </div>
                </div>
                <ExternalLink size={16} className="text-gray-400" />
              </div>
            </a>
            <a
              href="/refund"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-gray-800 p-4 rounded-lg border border-gray-700 hover:border-blue-500 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-blue-400" />
                  <div>
                    <div className="font-semibold text-white">Refund Policy</div>
                    <div className="text-xs text-gray-400">14-day premium refund, AI token rules</div>
                  </div>
                </div>
                <ExternalLink size={16} className="text-gray-400" />
              </div>
            </a>
            <a
              href="mailto:silosupport@gmail.com?subject=TTI%20Support"
              className="block bg-gray-800 p-4 rounded-lg border border-gray-700 hover:border-blue-500 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-blue-400" />
                  <div>
                    <div className="font-semibold text-white">Support email</div>
                    <div className="text-xs text-gray-400">silosupport@gmail.com</div>
                  </div>
                </div>
                <ExternalLink size={16} className="text-gray-400" />
              </div>
            </a>

            {/* Support Section */}
            <div className="pt-4 border-t border-gray-700">
              <h3 className="font-bold mb-3 text-white">Support</h3>
              <SupportSection />
            </div>

            {/* How to Use */}
            <div className="pt-4 border-t border-gray-700">
              <h3 className="font-bold mb-4">📖 How to Use Tabletop Inventory Manager</h3>
              <div className="space-y-6">

                {/* Getting Started */}
                <div>
                  <h4 className="font-medium text-blue-400 mb-2">🚀 Getting Started</h4>
                  <ul className="text-sm text-gray-400 space-y-1 pl-4">
                    <li>• Start by setting your character name and class in the <strong>Character</strong> tab</li>
                    <li>• Configure your ability scores (Strength determines carrying capacity)</li>
                    <li>• Roll for starting gold if desired, or manually set your currencies</li>
                    <li>• Use <strong>Quick Save</strong> to save your character progress</li>
                  </ul>
                </div>

                {/* Inventory Management */}
                <div>
                  <h4 className="font-medium text-green-400 mb-2">🎒 Inventory & Item Management</h4>
                  <ul className="text-sm text-gray-400 space-y-1 pl-4">
                    <li>• <strong>Add Items:</strong> Use the "+" button to add weapons, armor, gear, or starter packs</li>
                    <li>• <strong>Drag & Drop:</strong> Click and hold to drag items between inventory, containers, and vaults</li>
                    <li>• <strong>Right-click:</strong> Context menu for selling, editing, deleting, or opening items</li>
                    <li>• <strong>Double-click:</strong> Quick edit items or open containers (if in main inventory)</li>
                    <li>• <strong>Equipment:</strong> Right-click weapons/armor to equip/unequip to different slots</li>
                    <li>• <strong>Visual Feedback:</strong> Green = valid placement, Red = invalid or over limits</li>
                  </ul>
                </div>

                {/* Containers & Storage */}
                <div>
                  <h4 className="font-medium text-purple-400 mb-2">📦 Containers & Storage</h4>
                  <ul className="text-sm text-gray-400 space-y-1 pl-4">
                    <li>• <strong>Container Hierarchy:</strong> tiny → small → medium → large → huge (smaller fits in larger)</li>
                    <li>• <strong>Opening Containers:</strong> Double-click containers in main inventory to open side-by-side view</li>
                    <li>• <strong>Transfer Mode:</strong> When container is open, drag items between main inventory and container</li>
                    <li>• <strong>Weight Reduction:</strong> Some magical containers reduce the weight of contents</li>
                    <li>• <strong>Container Restrictions:</strong> Containers inside containers can be disabled in settings</li>
                    <li>• <strong>Quick Access:</strong> Use the container list below the main grid for easy opening</li>
                  </ul>
                </div>

                {/* Vaults */}
                <div>
                  <h4 className="font-medium text-yellow-400 mb-2">🏦 Vaults & Storage Locations</h4>
                  <ul className="text-sm text-gray-400 space-y-1 pl-4">
                    <li>• <strong>Create Vaults:</strong> Set up storage locations like home base, guild hall, etc.</li>
                    <li>• <strong>Vault Properties:</strong> Set location name, cost to access, and storage size</li>
                    <li>• <strong>Access Vaults:</strong> Click "Manage" to open vault and transfer items</li>
                    <li>• <strong>Export/Import:</strong> Share vault data between characters or campaigns</li>
                    <li>• <strong>Container Limitation:</strong> Containers in vaults cannot be opened (move to inventory first)</li>
                  </ul>
                </div>

                {/* Combat & Character Stats */}
                <div>
                  <h4 className="font-medium text-red-400 mb-2">⚔️ Combat & Character Features</h4>
                  <ul className="text-sm text-gray-400 space-y-1 pl-4">
                    <li>• <strong>Equipment Stats:</strong> View equipped armor AC, weapon attack bonuses automatically</li>
                    <li>• <strong>Spellcasting:</strong> Set spellcasting ability to calculate spell attack bonus and save DC</li>
                    <li>• <strong>Magic Items:</strong> Items with spell effects appear in the spells section</li>
                    <li>• <strong>Encumbrance:</strong> Monitor weight limits with color-coded progress bar</li>
                    <li>• <strong>Equipment Panel:</strong> Quick overview of worn armor and weapons</li>
                  </ul>
                </div>

                {/* Settings & Customization */}
                <div>
                  <h4 className="font-medium text-cyan-400 mb-2">⚙️ Settings & Customization</h4>
                  <ul className="text-sm text-gray-400 space-y-1 pl-4">
                    <li>• <strong>Weight Options:</strong> Toggle weight limits and currency weight</li>
                    <li>• <strong>Bag Size:</strong> Set custom inventory grid dimensions</li>
                    <li>• <strong>Selling:</strong> Adjust default sell ratio for items</li>
                    <li>• <strong>Auto-purchase:</strong> Automatically deduct gold when adding items</li>
                    <li>• <strong>Premium Mode:</strong> Enable for testing custom item images</li>
                    <li>• <strong>Character Management:</strong> Save, load, export, and import characters</li>
                  </ul>
                </div>

                {/* Advanced Features */}
                <div>
                  <h4 className="font-medium text-pink-400 mb-2">✨ Advanced Features</h4>
                  <ul className="text-sm text-gray-400 space-y-1 pl-4">
                    <li>• <strong>Shop Generator:</strong> DMs can generate random shops with roll tables</li>
                    <li>• <strong>Table View:</strong> Switch to spreadsheet view for easier bulk management</li>
                    <li>• <strong>Text Export:</strong> Copy inventory as formatted text for sharing</li>
                    <li>• <strong>Charge Tracking:</strong> Items with limited uses (wands, potions, etc.)</li>
                    <li>• <strong>Multi-Currency:</strong> Support for gold, silver, copper, and custom currencies</li>
                    <li>• <strong>Smart Validation:</strong> Prevents invalid item placement automatically</li>
                  </ul>
                </div>

                {/* Tips */}
                <div className="bg-gray-700 p-3 rounded">
                  <h4 className="font-medium text-amber-400 mb-2">💡 Pro Tips</h4>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Use Quick Save frequently to avoid losing progress</li>
                    <li>• Organize containers by type (weapons, supplies, treasure)</li>
                    <li>• Set up vaults for different locations (home, guild, inn)</li>
                    <li>• Right-click is your friend - it opens context menus everywhere</li>
                    <li>• Watch the encumbrance bar - being overloaded affects movement speed</li>
                    <li>• Export characters before major sessions for backup</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </CollapsibleSection>

        {/* ─── 8. Developer / Debug (dev-only) ─── */}
        {/* [PHASE B / #068 — 2026-05-07]: Kept as inline literal
            `process.env.NODE_ENV === 'development'` rather than the
            shared `IS_DEV_BUILD` constant. CRA's DefinePlugin replaces
            the literal at build time AND terser strips the dead
            JSX createElement call — verified empirically. The
            constant-import variant requires cross-module constant
            folding which CRA's Webpack config does NOT perform
            reliably for JSX, leaving the stripped section's
            "Enable Debug Mode" / "Premium Testing" strings in the
            production bundle. The non-JSX dev gates (SettingsContext
            default, usePremiumAccess override) still use IS_DEV_BUILD
            since they execute at runtime regardless and benefit from
            the single-source-of-truth refactor. See the trade-off
            documented in `src/utils/devMode.js`. */}
        {process.env.NODE_ENV === 'development' && (
          <CollapsibleSection
            title="Developer / Debug"
            icon={<Terminal size={16} />}
            storageKey="settings-developer"
            defaultCollapsed={true}
            headerClassName="bg-gray-700/50 rounded-lg"
          >
            <div className="space-y-4 mt-2">

              {/* Debug Mode Toggle */}
              <div className="mt-4">
                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={settings.showDebugTools}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      showDebugTools: e.target.checked
                    }))}
                    className="mr-2"
                  />
                  <span className="text-red-400 font-bold">Enable Debug Mode</span>
                </label>
              </div>

              {/* [SUB-PLAN 1B-i — 2026-05-23]: Legacy Inline Feat Creator toggle.
                  Re-enables the pre-Workshop modal that used to live inline in
                  FeatsPanel (now archived at
                  src/components/legacy/FeatsPanelInlineCreator.js). Default OFF;
                  flip ON to surface the legacy form for parity checks or to
                  recover the path if the Workshop-hosted creator regresses.
                  Dev-only: the entire enclosing section is gated on
                  NODE_ENV=development so this toggle never ships to production. */}
              <div className="mt-4">
                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={!!settings.showLegacyFeatInlineCreator}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      showLegacyFeatInlineCreator: e.target.checked
                    }))}
                    className="mr-2"
                  />
                  <span className="text-amber-400 font-semibold">Show Legacy Inline Feat Creator</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Re-enables the pre-Workshop custom-feat modal inside the Feats tab.
                  Use for parity comparisons against the Workshop creator.
                </p>
              </div>

              {/* Debug & Reset Tools (only when showDebugTools is enabled) */}
              {settings.showDebugTools && (
                <div className="pt-4 border-t border-red-700">
                  <h3 className="font-bold mb-4 text-red-400">🚨 DEBUG & RESET TOOLS</h3>
                  <div className="space-y-2">
                    <button
                      onClick={async () => {
                        if (await confirm({ title: 'Respec Character', message: 'This will reset your race, class, background, features, and proficiencies while keeping your name, inventory, and currency.\n\nYou will need to re-level your character from level 1.', confirmText: 'Respec', confirmColor: 'red' })) {
                          // Keep name, inventory, currencies, vaults, and some basic info
                          const preservedData = {
                            name: playerStats.name,
                            inspiration: playerStats.inspiration,
                            id: playerStats.id
                          };

                          // Reset to level 1 with preserved data (keeping inventory/currency intact)
                          setPlayerStats(prev => ({
                            ...prev,
                            ...preservedData,
                            level: 1,
                            experience: 0,
                            classes: [],
                            race: '',
                            class: '',
                            background: '',
                            subclass: '',
                            features: [],
                            feats: [],
                            proficiencies: {
                              armor: [],
                              weapons: [],
                              tools: [],
                              languages: [],
                              skills: {}
                            },
                            basics: {
                              race: '',
                              subrace: '',
                              class: '',
                              background: '',
                              alignment: prev.basics?.alignment || '',
                              experiencePoints: 0,
                              deity: prev.basics?.deity || '',
                              faction: prev.basics?.faction || ''
                            },
                            personality: {
                              traits: [],
                              ideals: [],
                              bonds: [],
                              flaws: []
                            },
                            // Reset ability scores to 10
                            strength: 10,
                            dexterity: 10,
                            constitution: 10,
                            intelligence: 10,
                            wisdom: 10,
                            charisma: 10,
                            // Reset HP
                            hp: 10,
                            maxHp: 10,
                            tempHp: 0,
                            hitDice: { current: 1, max: 1, size: 8 }
                          }));

                          // Clear spells
                          setKnownSpells([]);
                          setPreparedSpells([]);
                          setSpellSlots({});

                          // Keep inventory and currencies intact

                          showStatus('🔄 Character respecced! Use the Level Up or Character Creation wizard to rebuild your character.', 5000);
                          logActivity('Character respecced', 'character', { preservedName: preservedData.name });

                          // Prompt to open character wizard
                          setTimeout(async () => {
                            if (await confirm({ title: 'Rebuild Character', message: 'Would you like to open the Character Creation Wizard to rebuild your character?', confirmText: 'Open Wizard', confirmColor: 'blue' })) {
                              setShowCharacterWizard(true);
                            }
                          }, 1000);
                        }
                      }}
                      className="w-full bg-yellow-700 hover:bg-yellow-600 text-white p-2 rounded transition-colors font-bold"
                    >
                      🔄 RESPEC CHARACTER
                    </button>
                    <button
                      onClick={nuclearReset}
                      className="w-full bg-red-700 hover:bg-red-600 text-white p-2 rounded transition-colors"
                    >
                      🔥 NUCLEAR RESET - Clear All Data
                    </button>
                  </div>

                  {/* Premium Testing (Dev Only) */}
                  <div className="pt-4 border-t border-purple-700">
                    <h3 className="font-bold mb-4 text-purple-400">✨ Premium Testing (Dev Only)</h3>
                    <label className="flex items-center text-gray-300 mb-4">
                      <input
                        type="checkbox"
                        checked={settings.isPremium}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          isPremium: e.target.checked
                        }))}
                        className="mr-2"
                      />
                      Enable Premium Mode (for testing)
                    </label>

                    {settings.isPremium ? (
                      <div className="p-4 bg-gradient-to-r from-purple-900 to-pink-900 rounded">
                        <h4 className="font-bold mb-2">✨ Premium Active</h4>
                        <ul className="text-sm space-y-1">
                          <li>✓ Custom item images (double-click to edit)</li>
                          <li>✓ Upload your own icons</li>
                          <li>✓ AI image generation (would connect to API)</li>
                          <li>✓ Unlimited vaults</li>
                        </ul>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-700 rounded">
                        <h4 className="font-bold mb-2">Free Version</h4>
                        <p className="text-sm">Enable premium to test image features</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </CollapsibleSection>
        )}

      </div>

      {/* [TUTORIAL-1]: Browse / Replay Tours modal */}
      <TutorialHelpMenu
        isOpen={showTutorialHelpMenu}
        onClose={() => setShowTutorialHelpMenu(false)}
      />
    </div>
  );
}

// [SPEC D 2026-05-17]: Image Library section — local upload + Image Book export/import.
//   v1.0 stores base64 in playerStats.imageLibrary (schema v4). v1.x can swap
//   the storage backend to Capacitor FS / IndexedDB / localStorage fallback
//   without breaking the surface contract.
function ImageLibrarySection({ playerStats, setPlayerStats, showStatus }) {
  const images = playerStats?.imageLibrary?.images || [];
  const bytes = libraryBytes(playerStats);
  const mb = (bytes / (1024 * 1024)).toFixed(2);
  const [filterKind, setFilterKind] = useState('all');
  const [uploadKind, setUploadKind] = useState('portrait');

  const filteredImages = filterKind === 'all'
    ? images
    : images.filter((img) => img.kind === filterKind);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const data = await fileToBase64(file);
      const name = file.name.replace(/\.[^/.]+$/, '').slice(0, 80);
      setPlayerStats(addImage({ kind: uploadKind, name, data, tags: [] }));
      showStatus(`✅ Uploaded ${name}`, 2500);
    } catch (err) {
      showStatus(`❌ Upload failed: ${err.message}`, 4000);
    }
  };

  const handleRemove = (id, name) => {
    setPlayerStats(removeImage(id));
    showStatus(`🗑️ Removed ${name}`, 2000);
  };

  // [STONE 7 / #79 — 2026-05-18]: Promote a library portrait to the active
  // character.basics.portrait. The library entry stays — this is integration,
  // not replacement of the per-character portrait contract.
  const handleSetAsPortrait = (id, name) => {
    setPlayerStats(setAsActivePortrait(id));
    showStatus(`🖼️ Set "${name}" as active portrait`, 2500);
  };

  const handleExport = () => {
    const book = buildImageBook(playerStats, 'My Image Library');
    const blob = new Blob([JSON.stringify(book, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `image-book-${new Date().toISOString().slice(0, 10)}.imagebook.json`;
    a.click();
    URL.revokeObjectURL(url);
    showStatus(`📦 Exported ${book.images.length} image(s)`, 3000);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const before = images.length;
      setPlayerStats(importImageBook(bundle));
      // After re-render the count will update; show a generic success
      showStatus(`📥 Importing image book… (was ${before} images)`, 3000);
    } catch (err) {
      showStatus(`❌ Import failed: ${err.message}`, 4000);
    }
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="bg-gray-800 p-3 rounded text-sm text-gray-300 border border-gray-700">
        <div className="font-bold text-gray-200 mb-1">Local image storage for portraits, maps, and tokens.</div>
        <div className="text-xs text-gray-400">
          Images live on this device only. Export as an Image Book to share or back up;
          import a book to bring images into another character / install.
        </div>
        <div className="text-xs text-gray-400 mt-1">
          Library size: <span className={bytes > 20 * 1024 * 1024 ? 'text-amber-400' : 'text-emerald-300'}>{mb} MB</span>
          {bytes > 20 * 1024 * 1024 && <span className="ml-2 text-amber-400">⚠ approaching capacity</span>}
          {' · '}{images.length} image(s)
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs text-gray-400">Upload as:</label>
        <select
          value={uploadKind}
          onChange={(e) => setUploadKind(e.target.value)}
          className="bg-gray-700 text-gray-200 text-xs px-2 py-1 rounded border border-gray-600"
          data-testid="image-upload-kind"
        >
          <option value="portrait">Portrait</option>
          <option value="map">Map</option>
          {/* [STONE 7 / #72 — 2026-05-18]: expanded kinds — tokens for combat,
              item icons for shop / custom items, generic entity for NPCs. */}
          <option value="token">Token</option>
          <option value="item">Item Icon</option>
          <option value="entity">Entity (NPC)</option>
        </select>
        <label className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded text-sm cursor-pointer flex items-center gap-1">
          <Upload size={12} /> Upload Image
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="image-upload-input"
          />
        </label>
        <button
          type="button"
          onClick={handleExport}
          disabled={images.length === 0}
          className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded text-sm flex items-center gap-1"
          data-testid="image-book-export"
        >
          <Download size={12} /> Export Book
        </button>
        <label className="px-3 py-1.5 bg-purple-700 hover:bg-purple-600 text-white rounded text-sm cursor-pointer flex items-center gap-1">
          <Save size={12} /> Import Book
          <input
            type="file"
            accept=".json,.imagebook.json,application/json"
            onChange={handleImport}
            className="hidden"
            data-testid="image-book-import"
          />
        </label>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs text-gray-400">Filter:</label>
        {/* [STONE 7 / #72 — 2026-05-18]: filter buttons mirror upload-kind dropdown. */}
        {['all', 'portrait', 'map', 'token', 'item', 'entity'].map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => setFilterKind(kind)}
            className={`px-2 py-1 rounded text-xs ${filterKind === kind ? 'bg-amber-700 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >
            {kind === 'all' ? 'All' : kind.charAt(0).toUpperCase() + kind.slice(1)}
          </button>
        ))}
      </div>

      {filteredImages.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          {images.length === 0 ? 'No images yet. Upload a portrait or map to begin.' : `No ${filterKind} images.`}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {filteredImages.map((img) => (
            <div key={img.id} className="bg-gray-800 rounded border border-gray-700 overflow-hidden group">
              <div className="aspect-square bg-gray-900 flex items-center justify-center">
                <img
                  src={img.data}
                  alt={img.name}
                  className={`object-cover w-full h-full ${img.kind === 'portrait' ? 'rounded-full p-2' : ''}`}
                />
              </div>
              <div className="p-1.5 text-[10px] text-gray-300 truncate" title={img.name}>{img.name}</div>
              <div className="px-1.5 pb-1.5 flex items-center justify-between gap-1">
                <span className="text-[9px] text-gray-500 uppercase">{img.kind}</span>
                <div className="flex items-center gap-1">
                  {/* [STONE 7 / #79 — 2026-05-18]: Promote portrait → basics.portrait.
                      Integrates the library with the per-character portrait contract
                      WITHOUT replacing it (the library entry stays put). Only renders
                      for kind='portrait'. */}
                  {img.kind === 'portrait' && (
                    <button
                      type="button"
                      onClick={() => handleSetAsPortrait(img.id, img.name)}
                      className="text-emerald-400 hover:text-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      title={`Use "${img.name}" as your character portrait`}
                      data-testid={`image-set-as-portrait-${img.id}`}
                    >
                      <ImageIcon size={12} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(img.id, img.name)}
                    className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={`Remove ${img.name}`}
                    data-testid={`image-remove-${img.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
